/**
 * Phase O.3 — LeaseDetailTooltip.
 *
 * Hover/click panel showing the full lease record. Click anywhere in
 * the tooltip copies `lease_id` to the clipboard — admin support
 * workflow. Lightweight DOM (no portal); the parent positions it.
 */
import React, { useEffect, useRef, useState } from "react";

const _fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString();
};

const _relativeSeconds = (iso) => {
    const t = Date.parse(iso || "");
    if (!Number.isFinite(t)) return "—";
    const delta = Math.round((Date.now() - t) / 1000);
    if (delta < 0) return `in ${-delta}s`;
    if (delta < 60) return `${delta}s ago`;
    const m = Math.floor(delta / 60);
    return `${m}m ${delta % 60}s ago`;
};

export const LeaseDetailTooltip = ({ lease, open, onClose }) => {
    const [copied, setCopied] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose?.();
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open, onClose]);

    if (!open || !lease) return null;

    const expIn = (() => {
        const exp = Date.parse(lease.expires_at || "");
        if (!Number.isFinite(exp)) return "—";
        const s = Math.max(0, Math.floor((exp - Date.now()) / 1000));
        return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
    })();

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(lease.lease_id || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable */
        }
    };

    const Row = ({ label, value, mono = true, testId }) => (
        <div className="flex items-center gap-3 text-xs">
            <div className="w-32 text-zinc-500 uppercase tracking-wider text-[10px]">
                {label}
            </div>
            <div
                className={`${mono ? "font-mono" : ""} text-zinc-200 break-all`}
                data-testid={testId}
            >
                {value}
            </div>
        </div>
    );

    return (
        <div
            ref={ref}
            data-testid="lease-detail-tooltip"
            className="absolute z-50 mt-2 right-0 w-[420px] bg-zinc-950 border border-zinc-800 rounded-md shadow-2xl p-4 space-y-2"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Lease detail
                </div>
                <button
                    type="button"
                    onClick={copy}
                    data-testid="lease-copy-id-btn"
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-sm"
                >
                    {copied ? "Copied!" : "Copy lease_id"}
                </button>
            </div>
            <Row label="lease_id" value={lease.lease_id || "—"} testId="lease-detail-id" />
            <Row label="stage" value={lease.stage_key || "—"} />
            <Row label="project_id" value={lease.project_id || "—"} />
            <Row label="attempt" value={`${lease.attempt ?? 1}`} />
            <Row label="source" value={lease.source || "—"} mono={false} />
            <Row label="acquired_at" value={_fmt(lease.acquired_at)} />
            <Row
                label="expires_at"
                value={`${_fmt(lease.expires_at)} (in ${expIn})`}
            />
            <Row
                label="last_renewed_at"
                value={`${_fmt(lease.last_renewed_at)} (${_relativeSeconds(lease.last_renewed_at)})`}
            />
            <div className="pt-2 mt-2 border-t border-zinc-800 text-[10px] text-zinc-500">
                Lease is authoritative. Outputs are written only when the
                worker's CAS on this lease_id succeeds.
            </div>
        </div>
    );
};
